import { CalendarRange, ExternalLink, UserCog, Wallet } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type {
  DealContext,
  Instructor,
  ScheduledLessonRecord,
  Venue,
} from "../../../api/schemas.js";
import { amoLeadUrl } from "../../../lib/amo-url.js";
import type { AccessibleError } from "../model/types.js";
import { useViewportQuery } from "../model/useMobileCalendarView.js";
import { DealEnrollmentSetup } from "./DealEnrollmentSetup.js";
import { DealMoneyPanel } from "./DealMoneyPanel.js";
import { DealStudyPlan } from "./DealStudyPlan.js";
import { DealTransferDialog } from "./DealTransferDialog.js";

export type DealDrawerTab = "money" | "plan";

export interface DealDrawerProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /**
   * Какая вкладка открыта. Состояние поднято наверх намеренно: шторка закрывается, чтобы
   * показать урок, и `Sheet` при закрытии размонтирует содержимое. С неуправляемой вкладкой
   * менеджер, работавший в учебном плане, возвращался на «Деньги» после каждого урока —
   * а это самый частый цикл в карточке сделки.
   */
  readonly tab: DealDrawerTab;
  readonly onTabChange: (tab: DealDrawerTab) => void;
  readonly leadId: number;
  readonly context: DealContext | undefined;
  readonly hasEnrollment: boolean;
  /**
   * Курсы, по которым на сделке уже есть план — работающий или доведённый до конца.
   * Докупкой считается только то, чего в этом списке нет.
   */
  readonly enrolledCourseIds: readonly number[];
  /** Все уроки сделки по порядку — учебный план целиком, а не только неназначенные. */
  readonly lessons: readonly ScheduledLessonRecord[];
  /** Название курса по id зачисления — подписи секций учебного плана при докупке. */
  readonly enrollmentCourseNames?: ReadonlyMap<string, string>;
  /**
   * Названия курсов по элементу каталога 7631. Сейчас не используются: карточка «Как принимать
   * деньги» из шторки убрана. Проп оставлен до решения, куда девать само предписание —
   * `SettlementInstructionPanel` его ещё умеет ставить, и инструктор его читает.
   */
  readonly settlementCourseNames?: ReadonlyMap<number, string>;
  readonly venues: readonly Venue[];
  /** Кандидаты на передачу сделки — инструкторы этой локации. */
  readonly instructors: readonly Instructor[];
  /** Пояс локации — запасной для уроков без площадки в диалоге передачи. */
  readonly timeZone: string;
  /** Адрес аккаунта amo (`https://<аккаунт>.amocrm.ru`); без него ссылка не показывается. */
  readonly amoBaseUrl?: string | null;
  readonly enrollmentBusy: boolean;
  readonly onCreateEnrollment: (courseElementId?: number) => void;
  readonly onScheduleLesson: (lesson: ScheduledLessonRecord) => void;
  readonly onOpenLesson: (lesson: ScheduledLessonRecord) => void;
  readonly onError: (error: AccessibleError) => void;
}

/**
 * Всё второстепенное содержимое сделки — деньги и учебный план — живёт здесь, а не между
 * шапкой и сеткой: шторка перекрывает календарь, не отнимая у него высоту (правило 2
 * docs/frontend-architecture.md). Справа на широком экране, снизу во всю высоту на телефоне;
 * скролл один и принадлежит шторке.
 */
export function DealDrawer(props: DealDrawerProps) {
  const isPhone = useViewportQuery("(max-width: 639px)");
  const [transferOpen, setTransferOpen] = useState(false);
  const venueNames = new Map(props.venues.map((v) => [v.id, v.name]));
  const venueTimezones = new Map(props.venues.map((v) => [v.id, v.timezone]));
  const unscheduled = props.lessons.filter((l) => !l.startTime || l.status === "none");
  // Докупка: курс привязали к сделке в amo, а зачисления по нему ещё нет — карточка
  // создания возвращается в обе вкладки, но уже только с новыми курсами на выбор.
  // Доп. опции («Права») сюда не попадают: плана у них нет, и «докупить уроки» по ним нечего.
  const unenrolledCourses = (props.context?.courses ?? []).filter(
    (course) => !course.isAddon && !props.enrolledCourseIds.includes(course.elementId),
  );
  const addonSetup =
    props.context && props.hasEnrollment && unenrolledCourses.length > 0 ? (
      <DealEnrollmentSetup
        context={props.context}
        variant="addon"
        availableCourses={unenrolledCourses}
        busy={props.enrollmentBusy}
        onCreate={props.onCreateEnrollment}
      />
    ) : null;

  return (
    <>
      <DealTransferDialog
        open={transferOpen}
        leadId={props.leadId}
        dealName={props.context?.dealName ?? null}
        instructors={props.instructors}
        fallbackTimeZone={props.timeZone}
        onClose={() => setTransferOpen(false)}
        onTransferred={() => setTransferOpen(false)}
        onError={props.onError}
      />
      <Sheet open={props.open} onOpenChange={props.onOpenChange}>
        <SheetContent
          side={isPhone ? "bottom" : "right"}
          className={
            isPhone
              ? "h-dvh max-h-dvh gap-0 rounded-none"
              : "w-full gap-0 rounded-l-3xl sm:max-w-lg"
          }
          data-testid="deal-drawer"
        >
          <SheetHeader className="gap-1 border-b border-pad-200">
            <SheetTitle className="text-xl">
              {props.context?.dealName ?? `Сделка ${props.leadId}`}
            </SheetTitle>
            <SheetDescription className="flex flex-wrap items-center gap-x-2">
              lead {props.leadId}
              {props.amoBaseUrl ? (
                <a
                  className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
                  href={amoLeadUrl(props.amoBaseUrl, props.leadId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="deal-amo-link"
                >
                  Открыть в amoCRM
                  <ExternalLink className="size-3" />
                </a>
              ) : null}
            </SheetDescription>
          </SheetHeader>

          <Tabs
            value={props.tab}
            onValueChange={(value) => props.onTabChange(value as DealDrawerTab)}
            className="flex min-h-0 flex-1 flex-col gap-0"
          >
            <TabsList className="mx-3 mt-3 grid w-auto grid-cols-2 sm:mx-4">
              <TabsTrigger value="money" data-testid="deal-tab-money">
                <Wallet className="size-4" aria-hidden="true" />
                Деньги
              </TabsTrigger>
              <TabsTrigger value="plan" data-testid="deal-tab-plan">
                <CalendarRange className="size-4" aria-hidden="true" />
                Учебный план
                {unscheduled.length > 0 ? (
                  <span className="ml-1.5 rounded-full bg-primary px-1.5 font-semibold text-white text-xs">
                    {unscheduled.length}
                  </span>
                ) : null}
              </TabsTrigger>
            </TabsList>

            <TabsContent
              value="money"
              className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3 sm:p-4"
            >
              {/* Здесь только деньги. Карточка «создайте учебный план» стояла в обеих вкладках, и
                  в «Деньгах» она оказывалась над балансом — шторка просила сразу и урок, и оплату.
                  План живёт во вкладке плана, а на неё указывает счётчик на самой вкладке. */}
              <DealMoneyPanel
                leadId={props.leadId}
                context={props.context}
                onError={props.onError}
              />
            </TabsContent>

            <TabsContent
              value="plan"
              className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3 sm:p-4"
            >
              {props.context && !props.hasEnrollment ? (
                <DealEnrollmentSetup
                  context={props.context}
                  busy={props.enrollmentBusy}
                  onCreate={props.onCreateEnrollment}
                />
              ) : (
                <>
                  {addonSetup}
                  <DealStudyPlan
                    lessons={props.lessons}
                    venueNames={venueNames}
                    venueTimezones={venueTimezones}
                    {...(props.enrollmentCourseNames
                      ? { courseNames: props.enrollmentCourseNames }
                      : {})}
                    onSchedule={props.onScheduleLesson}
                    onOpen={props.onOpenLesson}
                  />
                  {/* Форс-мажор живёт рядом с планом, который он и меняет: инструктор выпал —
                    сделку ведёт другой, и уроки уезжают вместе с ней. */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    data-testid="deal-transfer-open"
                    onClick={() => setTransferOpen(true)}
                  >
                    <UserCog className="size-4" aria-hidden="true" />
                    Передать другому инструктору
                  </Button>
                </>
              )}
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>
    </>
  );
}
